import { useState } from "react";
import { Pencil } from "lucide-react";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, EnTeteCarte } from "@/composants/ui/carte";
import { Champ } from "@/composants/ui/champ";
import { Chargement, Erreur } from "@/composants/ui/etats";
import { Etiquette } from "@/composants/ui/etiquette";
import { messageDe, useChargement } from "@/lib/chargement";
import { horodatage } from "@/lib/libelles";
import { api, type SeuilAlerte } from "@/lib/api";

const UNITES: Record<SeuilAlerte["unite"], string> = { POURCENT: "%", JOURS: "jours", MO: "Mo" };

/** « 30 % », « 45 jours » : la valeur et son unité, à la française. */
function valeurLisible(seuil: SeuilAlerte) {
  return `${seuil.valeur.toLocaleString("fr-FR")} ${UNITES[seuil.unite]}`;
}

/**
 * Les seuils d'alerte, sous « Gérer les alertes » (LAB, 10/09 §05) : la liste,
 * et le réglage de chacun. Toute modification est tracée par la base — ancienne
 * valeur, nouvelle, auteur, date —, qui refuse une modification sans auteur.
 *
 * Ticket #50 (décision 48) : la tolérance sur les frais fournisseur en est un,
 * livrée à 0 % ; en deçà, un écart de frais est accepté d'office. L'écran ne
 * connaît aucun seuil par son code : un seuil de plus est une ligne de plus.
 */
export function PageSeuils() {
  const seuils = useChargement((signal) => api.seuilsAlerte(signal), []);
  const [courants, setCourants] = useState<Record<string, SeuilAlerte>>({});

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-xl font-semibold text-encre">Seuils</h1>
        <p className="mt-1 text-sm text-encre-douce">
          Les seuils d'alerte, la tolérance sur les frais fournisseur et la taille maximale d'une
          vidéo de chantier, réglables sans toucher au code. Chaque modification garde l'ancienne
          valeur, la nouvelle, son auteur et sa date. La tolérance et la taille des vidéos agissent
          dès aujourd'hui ; les seuils d'alerte serviront avec les alertes, dans un lot à venir.
        </p>
      </div>
      {seuils.erreur ? <Erreur message={seuils.erreur} onReessayer={seuils.recharger} /> : null}
      {!seuils.erreur && seuils.enCours ? <Chargement message="Chargement des seuils…" /> : null}
      {seuils.donnees ? (
        <Carte>
          <EnTeteCarte>
            <h2 className="text-sm font-semibold text-encre">Seuils d'alerte</h2>
          </EnTeteCarte>
          <ul className="divide-y divide-filet">
            {seuils.donnees.map((charge) => {
              const seuil = courants[charge.id] ?? charge;
              return (
                <li key={seuil.id} id={`seuil-${seuil.code}`} className="px-4 py-3">
                  <Seuil
                    seuil={seuil}
                    onRegle={(regle) => setCourants({ ...courants, [regle.id]: regle })}
                  />
                </li>
              );
            })}
          </ul>
        </Carte>
      ) : null}
    </div>
  );
}

function Seuil({
  seuil,
  onRegle,
}: {
  seuil: SeuilAlerte;
  onRegle: (seuil: SeuilAlerte) => void;
}) {
  const [enReglage, setEnReglage] = useState(false);
  const [valeur, setValeur] = useState(String(seuil.valeur));
  const [actif, setActif] = useState(seuil.actif);
  const [envoi, setEnvoi] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);

  if (!enReglage) {
    return (
      <article aria-label={seuil.libelle} className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <p className="min-w-0 flex-1 basis-60 text-sm text-encre">{seuil.libelle}</p>
        <span className="text-sm font-medium tabular-nums text-encre">{valeurLisible(seuil)}</span>
        {/* L'état se lit en toutes lettres (ADR-0024). */}
        <Etiquette ton={seuil.actif ? "actif" : "neutre"}>
          {seuil.actif ? "Actif" : "Désactivé"}
        </Etiquette>
        {seuil.en_service ? null : (
          <Etiquette ton="neutre">Sans effet avant le LOT des alertes</Etiquette>
        )}
        <span className="text-xs text-encre-douce">modifié le {horodatage(seuil.maj_le)}</span>
        <Bouton
          taille="compact"
          variante="discret"
          aria-label={`Régler : ${seuil.libelle}`}
          onClick={() => {
            setValeur(String(seuil.valeur));
            setActif(seuil.actif);
            setRefus(null);
            setEnReglage(true);
          }}
        >
          <Pencil className="h-3.5 w-3.5" aria-hidden />
          Régler
        </Bouton>
      </article>
    );
  }

  return (
    <form
      aria-label={`Régler : ${seuil.libelle}`}
      className="flex flex-col gap-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        setRefus(null);
        try {
          onRegle(await api.reglerSeuil(seuil.id, valeur, actif));
          setEnReglage(false);
        } catch (cause) {
          setRefus(messageDe(cause));
        } finally {
          setEnvoi(false);
        }
      }}
    >
      <p className="text-sm font-medium text-encre">{seuil.libelle}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Champ
          libelle={`Valeur (${UNITES[seuil.unite]})`}
          required
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={valeur}
          onChange={(e) => setValeur(e.target.value)}
        />
        <label
          className="flex min-h-11 cursor-pointer items-center gap-2 self-end text-sm text-encre"
        >
          <input
            type="checkbox"
            checked={actif}
            onChange={(e) => setActif(e.target.checked)}
            className="h-4 w-4 shrink-0 accent-laiton"
          />
          <span>Actif</span>
        </label>
      </div>
      {refus ? <Erreur message={refus} /> : null}
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" variante="principal" taille="compact" disabled={envoi}>
          {envoi ? "Enregistrement…" : "Enregistrer"}
        </Bouton>
        <Bouton
          type="button"
          variante="discret"
          taille="compact"
          disabled={envoi}
          onClick={() => setEnReglage(false)}
        >
          Annuler
        </Bouton>
      </div>
    </form>
  );
}
